import { pickBy } from 'Utils/lodashUtils';
import moment from 'moment';

import { getAvailableRanges } from 'Utils/dateUtils';

export function convertFilterObjectToArray(valuesObj = {}) {
  return Object.keys(valuesObj).map((key) => {
    return Object.assign({}, valuesObj[key], { id: parseInt(key, 10) });
  });
}

export function makeDateFilterValue(filterId, value1 = 'yesterday', value2) {
  const newFilter = {
    id: filterId,
    v1: value1,
  };

  if (value2) {
    newFilter.v2 = value2;
    newFilter.type = 'BETWEEN';
  } else {
    newFilter.type = 'STARTS_WITH';
  }

  return filterId ? newFilter : null;
}

export function parseTypesForFilters(filterValuesArr = [], dateFilters = [], dateFormat = 'MM/DD/YYYY') {
  return filterValuesArr.map((filterValue) => {
    const type = getFilterTypeByValues(filterValue);

    // make copy of the filter, before converting values to ISO date format
    const updatedFilter = Object.assign({}, filterValue, { type });

    const isDateFilter =
      dateFilters.findIndex((dateFilter) => dateFilter.reportFilterId === Number(updatedFilter.id)) > -1; // eslint-disable-line
    if (isDateFilter) {
      const isPresetFilter = ['yesterday', 'last_seven_days', 'this_month', 'last_month', 'this_year'].includes(
        updatedFilter.v1,
      );

      if (isPresetFilter) {
        updatedFilter.type = 'EXACT';
      } else {
        updatedFilter.v1 = moment(updatedFilter.v1, dateFormat).format('YYYY-MM-DD');
        updatedFilter.v2 = moment(updatedFilter.v2, dateFormat).format('YYYY-MM-DD');
      }
    }
    return updatedFilter;
  });
}

export function parseBackDateFilters(filtersWithType, dateFilters = [], dateFormat = 'MM/DD/YYYY') {
  const updatedFilter = Object.assign([], filtersWithType);
  return updatedFilter.map((filter) => {
    const filterValue = Object.assign({}, filter);
    const isDateFilter =
      dateFilters.findIndex((dateFilter) => dateFilter.reportFilterId === Number(filterValue.id)) > -1; // eslint-disable-line
    if (isDateFilter) {
      const isPresetFilter = ['yesterday', 'last_seven_days', 'this_month', 'last_month', 'this_year'].includes(
        filterValue.v1,
      );

      if (!isPresetFilter) {
        filterValue.v1 = moment(filterValue.v1, 'YYYY-MM-DD').format(dateFormat);
        filterValue.v2 = moment(filterValue.v2, 'YYYY-MM-DD').format(dateFormat);
      }
    }
    return filterValue;
  });
}

export function mapSavedFilters(filters, filtersToApply = []) {
  return filters.map((filter) => {
    const filterJson = filter.filterParams; // JSON.parse(filter.filterJson);
    const match = filtersToApply.filter((appliedFilter) => {
      return filterJson.some((f) => {
        if (f.id === appliedFilter.id) {
          // both of these will clear out an already applied filter if a new filter is
          // applied with different values.

          // if there is a singular value, it is equal, with no secondary value.
          if (f.v1 && f.v1 === appliedFilter.v1 && !f.v2) {
            return true;
          }

          // if there is a range of values, it is equal on both values.
          if (f.v1 && f.v2 && f.v1 === appliedFilter.v1 && f.v2 === appliedFilter.v2) {
            return true;
          }
        }

        return false;
      });
    });

    return Object.assign({}, filter, {
      isActive: match.length > 0 && match.length === filterJson.length ? match : false,
    });
  });
}

export function mapActiveFilters(filters = [], data = []) {
  const results = [];
  filters.forEach((filter) => {
    const matchingFilter = data.find((column) => column.elementId === filter.id);
    if (matchingFilter && matchingFilter.elementLabel) {
      results.push(
        Object.assign({}, filter, {
          name: matchingFilter.elementLabel,
          value: !filter.v2 ? filter.v1 : null,
          values: filter.v2 ? { v1: filter.v1, v2: filter.v2 } : null,
          v1: filter.v1,
          v2: filter.v2,
        }),
      );
    }
  });

  return results;
}

export function normalizeDateFilters(
  filterDefs = [],
  filterVals = [],
  dateFormat = 'MM/DD/YYYY',
  isDynamicFilter = false,
) {
  const results = filterVals.map((val) => {
    const matchingDef = filterDefs.find((def) => {
      return isDynamicFilter ? def.updatedId === val.id : def.elementId === val.id;
    });

    if (matchingDef && matchingDef.elementTypeName !== 'DATE') {
      return Object.assign({}, val, {
        elementCategoryName: matchingDef.elementCategoryName,
        elementTypeName: matchingDef.elementTypeName,
      });
    } else if (matchingDef) {
      const emptyTranslations = {};
      const availableRanges = getAvailableRanges(emptyTranslations);
      const presetRangeObject = availableRanges.find((range) => range.value === val.v1 || range.value === val.v2);
      if (presetRangeObject) {
        return val;
      } else {
        const v1 = moment(val.v1, dateFormat).isValid() ? val.v1 : '';
        const v2 = moment(val.v2, dateFormat).isValid() ? val.v2 : '';
        return Object.assign({}, val, {
          v1,
          v2,
          elementCategoryName: matchingDef.elementCategoryName,
          elementTypeName: matchingDef.elementTypeName,
        });
      }
    }
  });

  return results;
}

export function getFilterTypeByValues(filterValue) {
  if (filterValue.v1 && !filterValue.v2) {
    return 'STARTS_WITH';
  } else if (!filterValue.v1 && filterValue.v2) {
    return 'ENDS_WITH';
  } else {
    return 'BETWEEN';
  }
}

export function filterArrayKeys(array) {
  return array.map((item) => {
    return filterObject(item);
  });
}

export function parseDateFilters(filtersWithType, dateFilters = [], dateFormat = 'MM/DD/YYYY') {
  const updatedFilter = Object.assign([], filtersWithType);
  return updatedFilter.map((filter) => {
    const filterValue = Object.assign({}, filter);
    const isDateFilter =
      dateFilters.findIndex((dateFilter) => dateFilter.reportFilterId === Number(filterValue.id)) > -1; // eslint-disable-line
    if (isDateFilter) {
      const isPresetFilter = ['yesterday', 'last_seven_days', 'this_month', 'last_month', 'this_year'].includes(
        filterValue.v1,
      );

      if (!isPresetFilter) {
        filterValue.v1 =
          moment(filterValue.v1, 'YYYY-MM-DD').format('YYYY-MM-DD') === filterValue.v1
            ? filterValue.v1
            : moment(filterValue.v1, dateFormat).format('YYYY-MM-DD');
        filterValue.v2 =
          moment(filterValue.v2, 'YYYY-MM-DD').format('YYYY-MM-DD') === filterValue.v2
            ? filterValue.v2
            : moment(filterValue.v2, dateFormat).format('YYYY-MM-DD');
      }
    }
    return filterValue;
  });
}
/**
 * only return the allowed keys for a filter-value object
 * @param  {object} item  a filter-value object with any keys, including id, v1, v2, and type
 * @return {object}       the filter-value object with ONLY the keys id, v1, v2, and type
 *
 * @private
 */
export function filterObject(item) {
  const allowedKeys = ['id', 'v1', 'v2', 'type'];

  return pickBy(item, (value, key) => {
    return allowedKeys.includes(key);
  });
}

export function removeValidationsFilters(filtersWithType) {
  const updatedFilter = Object.assign([], filtersWithType);
  updatedFilter.filters.map((item) => {
    if (item.hasOwnProperty('flag')) {
      delete item.flag;
    }
    if (item.hasOwnProperty('error')) {
      delete item.error;
    }
  });
  return Object.assign({}, updatedFilter);
}

export function removeValidationsFiltersGlobal(filtersWithType) {
  const updatedFilter = Object.assign([], filtersWithType);
  updatedFilter.map((item) => {
    if (item.hasOwnProperty('flag')) {
      delete item.flag;
    }
    if (item.hasOwnProperty('error')) {
      delete item.error;
    }
  });
  return Object.assign({}, updatedFilter);
}

export default {
  convertFilterObjectToArray,
  makeDateFilterValue,
  mapSavedFilters,
  mapActiveFilters,
  normalizeDateFilters,
  getFilterTypeByValues,
  filterArrayKeys,
  parseTypesForFilters,
  parseDateFilters,
  removeValidationsFilters,
  removeValidationsFiltersGlobal,
};
